import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Star, Minus, Plus } from "lucide-react";
import { formatPrice } from "@/lib/format";

export const QuickView = ({ product, onClose, onAdd }) => {
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [colorIdx, setColorIdx] = useState(0);

  useEffect(() => {
    if (product) { setQty(1); setActiveImg(0); setColorIdx(0); }
  }, [product]);

  const colors = product?.colors || null;
  const gallery = product
    ? colors
      ? colors.map((c) => c.image)
      : product.images?.length ? product.images : [product.image]
    : [];

  const bundle = product?.bundle;
  const displayTotal = product
    ? bundle && qty >= bundle.qty
      ? Math.floor(qty / bundle.qty) * bundle.price + (qty % bundle.qty) * product.price
      : product.price * qty
    : 0;

  const handleAdd = () => {
    if (colors) {
      const c = colors[colorIdx];
      onAdd({ ...product, id: `${product.id}-${c.name.toLowerCase()}`, name: `${product.name} · ${c.name}`, baseName: product.name, image: c.image }, qty);
    } else {
      onAdd(product, qty);
    }
  };

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          data-testid="quick-view-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/50 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative grid max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white shadow-2xl dark:bg-stone-900 md:grid-cols-2"
          >
            <button
              data-testid="quick-view-modal-close"
              onClick={onClose}
              aria-label="Cerrar vista rápida"
              className="absolute right-4 top-4 z-10 rounded-full bg-white/80 p-2 text-stone-600 backdrop-blur-sm transition-colors hover:text-stone-900 dark:bg-stone-800/80 dark:text-stone-300 dark:hover:text-white"
            >
              <X size={18} />
            </button>
            <div className="relative min-h-64 bg-[#F4F0EA] dark:bg-[#1C1917]">
              <img src={gallery[activeImg]} alt={product.name} className={`h-full w-full ${product.contain ? "object-contain p-6" : "object-cover"}`} />
              <span className="absolute left-4 top-4 rounded-full bg-white/85 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-stone-700 dark:bg-stone-900/85 dark:text-stone-200">
                {product.badge}
              </span>
              {gallery.length > 1 && !colors && (
                <div className="absolute bottom-4 left-4 flex gap-2">
                  {gallery.map((img, i) => (
                    <button
                      key={i}
                      data-testid={`quick-view-thumb-${i}`}
                      onClick={() => setActiveImg(i)}
                      aria-label={`Ver imagen ${i + 1}`}
                      className={`h-14 w-14 overflow-hidden rounded-xl border-2 transition-all duration-300 ${
                        activeImg === i ? "border-amber-600" : "border-white/60 opacity-70 hover:opacity-100"
                      }`}
                    >
                      <img src={img} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col p-8">
              <div className="flex items-center gap-2 text-xs text-stone-600 dark:text-stone-300">
                <Star size={12} className="fill-amber-500 text-amber-500" />
                {product.rating} · {product.reviews_count} reseñas
              </div>
              <h3 className="mt-2 font-serif text-3xl font-light text-stone-900 dark:text-stone-50">{product.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-stone-600 dark:text-stone-300">{product.description}</p>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-stone-500 dark:text-stone-400">
                {product.material}
              </p>

              {colors && (
                <>
                  <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500 dark:text-stone-400">
                    Color — {colors[colorIdx].name}
                  </p>
                  <div className="mt-2 flex gap-3">
                    {colors.map((c, i) => (
                      <button
                        key={c.name}
                        data-testid={`quick-view-color-${c.name.toLowerCase()}`}
                        onClick={() => { setColorIdx(i); setActiveImg(i); }}
                        aria-label={`Color ${c.name}`}
                        className={`h-14 w-14 overflow-hidden rounded-xl border-2 transition-all duration-300 ${
                          colorIdx === i ? "border-amber-600 ring-2 ring-amber-600/30" : "border-stone-200 opacity-70 hover:opacity-100 dark:border-stone-700"
                        }`}
                      >
                        <img src={c.image} alt={c.name} className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                </>
              )}

              {bundle && (
                <>
                  <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-700 dark:text-amber-400">
                    Promo del mes · 2 piezas por {formatPrice(bundle.price)} · puedes combinar colores Oro, Plata y Negro
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      data-testid="quick-view-pack-single"
                      onClick={() => setQty(1)}
                      className={`rounded-full px-4 py-2 text-xs font-medium transition-all duration-300 ${
                        qty !== bundle.qty
                          ? "bg-stone-900 text-[#FDFBF7] dark:bg-stone-50 dark:text-stone-900"
                          : "border border-stone-200 text-stone-600 hover:border-stone-400 dark:border-stone-700 dark:text-stone-300"
                      }`}
                    >
                      1 pieza — {formatPrice(product.price)}
                    </button>
                    <button
                      data-testid="quick-view-pack-bundle"
                      onClick={() => setQty(bundle.qty)}
                      className={`rounded-full px-4 py-2 text-xs font-medium transition-all duration-300 ${
                        qty === bundle.qty
                          ? "bg-amber-600 text-white"
                          : "border border-amber-500/60 text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-500/10"
                      }`}
                    >
                      {bundle.qty} piezas — {formatPrice(bundle.price)} · ahorras {formatPrice(product.price * bundle.qty - bundle.price)}
                    </button>
                  </div>
                </>
              )}

              <div className="mt-auto flex items-center justify-between gap-4 pt-8">
                <div className="flex items-center gap-3 rounded-full border border-stone-200 px-2 py-1 dark:border-stone-700">
                  <button data-testid="quick-view-qty-decrement" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-1.5 text-stone-500 hover:text-stone-900 dark:hover:text-white" aria-label="Reducir cantidad"><Minus size={14} /></button>
                  <span data-testid="quick-view-qty" className="w-6 text-center font-mono text-sm">{qty}</span>
                  <button data-testid="quick-view-qty-increment" onClick={() => setQty((q) => q + 1)} className="p-1.5 text-stone-500 hover:text-stone-900 dark:hover:text-white" aria-label="Aumentar cantidad"><Plus size={14} /></button>
                </div>
                <p className="text-xl font-semibold text-stone-900 dark:text-stone-50">{formatPrice(displayTotal)}</p>
              </div>
              <button
                data-testid="quick-view-add-to-cart"
                onClick={handleAdd}
                className="mt-4 w-full rounded-full bg-stone-900 py-3.5 text-sm font-medium text-[#FDFBF7] transition-colors duration-300 hover:bg-amber-700 dark:bg-stone-50 dark:text-stone-900 dark:hover:bg-amber-400"
              >
                Añadir al carrito
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
